import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tag as TagIcon, Plus, X } from "lucide-react";

/**
 * v5.7 Addition 12 — Tag apply/remove UI on Deal Detail.
 * Shows currently applied tags as coloured badges + a dropdown to add more
 * from the pipeline's tag library.
 */
export default function DealTagsBar({ dealId, pipelineId }) {
  const [applied, setApplied] = useState([]);
  const [library, setLibrary] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  const load = async () => {
    if (!dealId || !pipelineId) return;
    const [{ data: a }, { data: l }] = await Promise.all([
      api.get(`/deals/${dealId}/tags`),
      api.get("/tags", { params: { pipeline_id: pipelineId } }),
    ]);
    setApplied(a || []);
    setLibrary(l || []);
  };

  useEffect(() => { load(); }, [dealId, pipelineId]); // eslint-disable-line

  const apply = async (tag_id) => {
    try {
      await api.post(`/deals/${dealId}/tags`, { tag_id });
      toast.success("Tag applied");
      setShowPicker(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (tag_id) => {
    await api.delete(`/deals/${dealId}/tags/${tag_id}`);
    toast.success("Tag removed");
    load();
  };

  const appliedIds = new Set(applied.map((a) => a.tag_id));
  const available = library.filter((t) => !appliedIds.has(t.tag_id));

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="deal-tags-bar">
      {applied.map((a) => (
        <span key={a.application_id}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
          style={{ background: a.tag?.tag_color, color: "#fff" }}
          data-testid={`applied-tag-${a.tag?.tag_name}`}>
          <TagIcon size={10} /> {a.tag?.tag_name}
          <button className="ml-1 hover:opacity-80" onClick={() => remove(a.tag_id)}>
            <X size={10} />
          </button>
        </span>
      ))}
      <div className="relative">
        <button
          onClick={() => setShowPicker((v) => !v)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border border-dashed"
          style={{ borderColor: "var(--cw-border)", color: "var(--cw-muted)" }}
          data-testid="tag-picker-btn"
        >
          <Plus size={10} /> Tag
        </button>
        {showPicker && (
          <div
            className="absolute z-30 top-full left-0 mt-1 min-w-[200px] rounded-md shadow-lg border bg-white"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="tag-picker-menu"
          >
            {available.length === 0 && (
              <div className="text-xs text-[var(--cw-muted)] px-3 py-2 italic">
                No more tags available
              </div>
            )}
            {available.map((t) => (
              <button
                key={t.tag_id}
                onClick={() => apply(t.tag_id)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-black/5 flex items-center gap-2"
                data-testid={`tag-picker-option-${t.tag_name}`}
              >
                <span className="h-3 w-3 rounded-full" style={{ background: t.tag_color }} />
                {t.tag_name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
